import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioSection, NioButton } from '../../../components';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

function RequestDemandStep10() {
  const MAX_DETAILS_LENGTH = 1395;
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceId } = useParams();
  const { t } = useTranslation();

  const selectedSubService = location.state?.selectedSubService || t('common.subService');
  const parentServiceTitle = location.state?.parentServiceTitle || t('common.service');
  const projectTitle = location.state?.projectTitle || '';
  const requestDetails = location.state?.requestDetails || {};
  const requestStep4Details = location.state?.requestStep4Details || {};
  const requestStep5Details = location.state?.requestStep5Details || {};
  const requestStep6Details = location.state?.requestStep6Details || {};
  const requestStep7Details = location.state?.requestStep7Details || {};
  const requestStep8Details = location.state?.requestStep8Details || {};
  const requestStep9Details = location.state?.requestStep9Details || {};
  const isStep5Skipped = location.state?.isStep5Skipped || false;
  const displayStep = isStep5Skipped ? 9 : 10;
  const [additionalDetails, setAdditionalDetails] = useState('');

  const averagePriceText = useMemo(() => {
    const budget = requestStep9Details?.budget;
    return budget ? `${budget} TND` : t('wizard.step9OnQuote');
  }, [requestStep9Details, t]);
  const summaryImageUrl = requestStep5Details?.selectedImageUrl || '/images/carrelage.jpg';
  const canContinue = Boolean(additionalDetails.trim());

  return (
    <AppLayout title={`${t('wizard.step', { n: displayStep })} - ${t('wizard.step10Title')}`} rootClass="layout-1">
      <style>{`
        .request-step10-page {
          background: #f2f3f5;
          min-height: 78vh;
          border-radius: 14px;
          position: relative;
          display: flex;
          flex-direction: column;
        }
        .request-step10-content {
          flex: 1 1 auto;
          width: 100%;
          max-width: 760px;
          margin: 0 auto;
          padding-top: 4.2rem;
          padding-bottom: 2rem;
        }
        .request-step10-step {
          color: #697586;
          font-size: 0.9rem;
          margin-bottom: 0.25rem;
        }
        .request-step10-heading {
          color: #0f2b3d;
          font-weight: 700;
          margin-bottom: 0.45rem;
        }
        .request-step10-description {
          color: #5f6b7a;
          margin-bottom: 2rem;
        }
        .request-step10-card {
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          overflow: hidden;
          margin-bottom: 2rem;
        }
        .request-step10-card-image-wrap {
          position: relative;
          height: 180px;
        }
        .request-step10-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .request-step10-card-btn {
          position: absolute;
          top: 12px;
          left: 12px;
          border: none;
          border-radius: 6px;
          background: #fff;
          color: #0f2b3d;
          font-size: 0.7rem;
          font-weight: 600;
          padding: 0.35rem 0.55rem;
        }
        .request-step10-card-body {
          padding: 0.6rem 0.75rem 0.75rem;
        }
        .request-step10-card-title {
          color: #284b63;
          font-size: 1.15rem;
          font-weight: 700;
          text-transform: uppercase;
          margin-bottom: 0.2rem;
        }
        .request-step10-card-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.75rem;
        }
        .request-step10-card-subtitle {
          color: #0f2b3d;
          font-weight: 500;
        }
        .request-step10-card-price {
          color: #0f2b3d;
          font-weight: 700;
        }
        .request-step10-extra-label {
          color: #0f2b3d;
          font-size: 1.05rem;
          font-weight: 500;
          margin-bottom: 0.7rem;
        }
        .request-step10-textarea {
          width: 100%;
          min-height: 120px;
          border: 1px solid #d6dde5;
          border-radius: 8px;
          background: #fff;
          color: #0f2b3d;
          padding: 0.8rem;
          resize: none;
        }
        .request-step10-counter {
          text-align: right;
          color: #a3adba;
          font-size: 0.84rem;
          margin-top: 0.35rem;
        }
        .request-step10-footer {
          flex-shrink: 0;
          margin-top: auto;
          border-top: 1px solid #dde3ea;
          padding: 0.7rem 1.1rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #f8f9fb;
          border-bottom-left-radius: 14px;
          border-bottom-right-radius: 14px;
          z-index: 5;
          box-shadow: 0 -4px 16px rgba(15, 43, 61, 0.08);
        }
      `}</style>

      <NioSection className="pt-120 pt-lg-160 pb-4">
        <NioSection.Content>
          <div className="request-step10-page p-3 p-md-4">
            <div className="request-step10-content">
              <div className="request-step10-step">{t('wizard.step', { n: displayStep })}</div>
              <h2 className="request-step10-heading">{t('wizard.step10Heading')}</h2>
              <p className="request-step10-description">
                {t('wizard.step10Description')}
              </p>

              <div className="request-step10-card">
                <div className="request-step10-card-image-wrap">
                  <img className="request-step10-card-image" src={summaryImageUrl} alt={t('wizard.step10ImageAlt')} />
                  <button type="button" className="request-step10-card-btn">{t('wizard.step10ViewDetails')}</button>
                </div>
                <div className="request-step10-card-body">
                  <div className="request-step10-card-title">{(projectTitle || t('wizard.step10DefaultTitle')).toUpperCase()}</div>
                  <div className="request-step10-card-row">
                    <div className="request-step10-card-subtitle">{translateServiceLabel(t, selectedSubService) || translateServiceLabel(t, parentServiceTitle)}</div>
                    <div className="request-step10-card-price">{averagePriceText}</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="request-step10-extra-label">{t('wizard.step10ExtraLabel')}</h3>
                <textarea
                  className="request-step10-textarea"
                  maxLength={MAX_DETAILS_LENGTH}
                  value={additionalDetails}
                  onChange={(event) => setAdditionalDetails(event.target.value)}
                />
                <div className="request-step10-counter">{additionalDetails.length} / {MAX_DETAILS_LENGTH}</div>
              </div>
            </div>

            <div className="request-step10-footer">
              <NioButton
                className="btn btn-primary"
                label={t('common.back')}
                onClick={() => navigate(-1)}
              />
              <NioButton
                className={`btn ${canContinue ? 'btn-primary' : 'btn-primary disabled'}`}
                label={t('common.next')}
                onClick={() => {
                  if (!canContinue) return;
                  navigate(`/service/${serviceId}/request-demand-step11`, {
                    state: {
                      selectedSubService,
                      parentServiceTitle,
                      projectTitle,
                      isStep5Skipped,
                      requestDetails,
                      requestStep4Details,
                      requestStep5Details,
                      requestStep6Details,
                      requestStep7Details,
                      requestStep8Details,
                      requestStep9Details,
                      requestStep10Details: {
                        additionalDetails: additionalDetails.trim(),
                      },
                    },
                  });
                }}
              />
            </div>
          </div>
        </NioSection.Content>
      </NioSection>
    </AppLayout>
  );
}

export default RequestDemandStep10;
